import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Loader2, Trash2, Plus, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { useCompany } from "@/context/CompanyContext";

/**
 * Iter153: Mitarbeiter-Nummern pro Firma.
 * Owner/Advisor kann WhatsApp-Nummern von Mitarbeitern registrieren, die dann
 * ohne eigenen Login Belege via WhatsApp einreichen können.
 */
export function CompanyUploaderPhonesSection({ canWrite }) {
  const { company } = useCompany();
  const companyId = company?._id;
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ phone_e164: "", employee_name: "" });

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/uploader-phones`);
      setPhones(r.data || []);
    } catch (e) {
      // silent — nicht alle User haben Zugriff
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.phone_e164.trim() || !form.employee_name.trim()) {
      toast.error("Bitte Nummer und Mitarbeiter-Name eintragen.");
      return;
    }
    setAdding(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/uploader-phones`, {
        phone_e164: form.phone_e164.trim(),
        employee_name: form.employee_name.trim(),
      });
      setPhones((p) => [...p, r.data]);
      setForm({ phone_e164: "", employee_name: "" });
      toast.success(`${r.data.employee_name} kann jetzt Belege via WhatsApp senden.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hinzufügen fehlgeschlagen");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (phoneId, name) => {
    if (!window.confirm(`Nummer von ${name} wirklich entfernen?`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/uploader-phones/${phoneId}`);
      setPhones((p) => p.filter((x) => x.id !== phoneId));
      toast.success("Entfernt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleToggle = async (phone) => {
    try {
      const r = await apiClient.patch(
        `/companies/${companyId}/uploader-phones/${phone.id}`,
        { active: !phone.active },
      );
      setPhones((p) => p.map((x) => (x.id === phone.id ? r.data : x)));
      toast.success(r.data.active ? "Aktiviert" : "Deaktiviert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Umschalten fehlgeschlagen");
    }
  };

  if (!companyId) return null;

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="uploader-phones-section">
      <div className="flex items-start gap-2 mb-3">
        <Users className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-xs font-semibold text-slate-800">
            👥 Mitarbeiter-Nummern für {company?.name || "diese Firma"}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            Nummern von Mitarbeitern, die Belege via WhatsApp einreichen dürfen — ohne eigenen App-Login.
            Ideal für Mitarbeiter mit Firmenkarte: einfach das Foto der Quittung an den Bot schicken.
          </p>
        </div>
      </div>

      {canWrite && (
        <form onSubmit={handleAdd} className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="uploader-phone-form">
          <div>
            <Label className="text-[10px]">Mobilnummer (+41…)</Label>
            <Input
              value={form.phone_e164}
              onChange={(e) => setForm({ ...form, phone_e164: e.target.value })}
              placeholder="+41 79 111 22 33"
              className="text-xs"
              data-testid="uploader-phone-input"
            />
          </div>
          <div>
            <Label className="text-[10px]">Mitarbeiter-Name</Label>
            <Input
              value={form.employee_name}
              onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
              placeholder="z.B. Max Muster"
              className="text-xs"
              data-testid="uploader-name-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              size="sm"
              disabled={adding || !form.phone_e164 || !form.employee_name}
              className="rounded-full bg-emerald-600 text-white w-full"
              data-testid="uploader-add-btn"
            >
              {adding ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
              Hinzufügen
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Lade …
        </div>
      ) : phones.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic" data-testid="uploader-empty">
          Noch keine Mitarbeiter-Nummer registriert.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="uploader-phones-list">
          {phones.map((p) => (
            <li
              key={p.id}
              className={`flex items-center justify-between gap-2 p-2 rounded ${p.active ? "bg-slate-50" : "bg-slate-100 opacity-60"}`}
              data-testid={`uploader-phone-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate">
                  <span className="font-mono">{p.phone_e164}</span>
                  <span className="text-slate-500"> · </span>
                  <span>{p.employee_name}</span>
                </div>
                <div className="text-[10px] text-slate-500">
                  {p.active ? "aktiv" : "deaktiviert"}
                  {p.created_at && ` · seit ${new Date(p.created_at).toLocaleDateString("de-CH")}`}
                </div>
              </div>
              {canWrite && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => handleToggle(p)}
                    data-testid={`uploader-toggle-${p.id}`}
                  >
                    {p.active ? <PowerOff className="h-3 w-3" /> : <Power className="h-3 w-3" />}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-rose-700 hover:bg-rose-50"
                    onClick={() => handleDelete(p.id, p.employee_name)}
                    data-testid={`uploader-delete-${p.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
